import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { useEffect, useState } from 'react'
import { LineChart } from 'react-gifted-charts'

import { SubstanceObj } from '../../providers/SubstanceCounter/SubstanceCounter.Provider'
import { themeColors } from '../../theme'
import { getMonthNameByNum } from '../../utilities/HelperFunctions'
import { isFloat } from './PatientChartPage'
import { useStyles } from './PatientChartPage.styles'

interface LineDataItem {
  value: number
  label?: string
  hideDataPoint?: boolean
  dataPointText?: number
}
interface LineSegment {
  startIndex: number
  endIndex: number
  color?: string
  thickness?: number
  strokeDashArray?: number[]
}

interface SubstanceRecord {
  trackedSubstanceId: number
  intakeAmount: number
  recordDate: string
  trackedSubstance: {
    dosageUnit: {
      unitName: string
    }
    substance: {
      name: string
    }
  }
}

interface MyJourneyChartInterface {
  timeRangeOptions: string[]
  selectedTab: string
  selectedRecordsForGraph: any[]
  selectedTimeRange: string
  setSelectedTimeRange: React.Dispatch<React.SetStateAction<string>>
  viewBacChartTab: string
}

export const MyJourneyChart = ({
  timeRangeOptions,
  selectedTab,
  selectedRecordsForGraph,
  selectedTimeRange,
  setSelectedTimeRange,
  viewBacChartTab,
}: MyJourneyChartInterface) => {
  const { classes } = useStyles()

  dayjs.extend(weekOfYear)

  // Color palette for different substances
  // const substanceColors = [
  //   themeColors.green,
  //   themeColors.darkPurple,
  //   themeColors.red,
  //   themeColors.tiffany,
  //   themeColors.blue,
  //   themeColors.indigo,
  //   themeColors.orange,
  //   themeColors.yellow,
  //   themeColors.teal50,
  //   themeColors.gold,
  // ]

  // State for substance selection when tab is SubstanceCounter
  const [selectedSubstance, setSelectedSubstance] = useState<number | null>(
    null
  )
  const [availableSubstances, setAvailableSubstances] = useState<
    { id: number; name: string; unit: string }[]
  >([])

  // Extract unique substances when selectedTab is SubstanceCounter
  useEffect(() => {
    if (selectedTab === 'SubstanceCounter') {
      const substances = selectedRecordsForGraph.reduce(
        (
          acc: { id: number; name: string; unit: string }[],
          record: SubstanceRecord
        ) => {
          const existingSubstance = acc.find(
            (s) => s.id === record.trackedSubstanceId
          )
          if (!existingSubstance && record.trackedSubstance) {
            const unitName = record.trackedSubstance.dosageUnit?.unitName || ''
            const unit = unitName.split(' ')[1] || 'mg' // Extract unit (mg, g, etc.)
            // xconst colorIndex = acc.length % substanceColors.length
            acc.push({
              id: record.trackedSubstanceId,
              name:
                record.trackedSubstance.substance?.name ||
                `Substance ${record.trackedSubstanceId}`,
              unit: unit,
              // color: substanceColors[colorIndex],
            })
          }
          return acc
        },
        []
      )

      setAvailableSubstances(substances)

      // Auto-select first substance initially if none selected
      if (
        substances.length > 0 &&
        !substances.some((s: SubstanceObj) => s.id === selectedSubstance)
      ) {
        setSelectedSubstance(substances[0].id)
      }
    } else {
      // Reset when leaving SubstanceCounter tab
      setSelectedSubstance(null)
      setAvailableSubstances([])
    }
  }, [selectedTab, selectedRecordsForGraph])

  // Get the primary SubstanceUnit for y-axis
  const getPrimarySubstanceUnit = () => {
    if (
      selectedTab !== 'SubstanceCounter' ||
      availableSubstances.length === 0 ||
      selectedSubstance === null
    )
      return 'mg'

    const substance = availableSubstances.find(
      (s) => s.id === selectedSubstance
    )
    return substance?.unit || 'mg'
  }

  // Handle substance selection changes (radio button style)
  const handleSubstanceSelection = (substanceId: number) => {
    setSelectedSubstance(substanceId)
  }

  // Calculate total intake amount for substance records
  const calculateTotalIntake = (record: SubstanceRecord) => {
    const unitMultiplier = parseFloat(
      record.trackedSubstance?.dosageUnit?.unitName?.split(' ')[0] || '1'
    )
    return unitMultiplier * record.intakeAmount
  }

  const renderXLabels = () => {
    const numOfMonths = Array.from(Array(6).keys())
    const monthNumArr = numOfMonths.map((num) =>
      dayjs().subtract(num, 'month').get('month')
    )
    const monthNameArr = monthNumArr.sort().map((num) => getMonthNameByNum(num))

    switch (selectedTimeRange) {
      case '6m': // past 6 months
        return monthNameArr

      case '1m': {
        //past 31 days
        const numOfDays31 = Array.from(Array(31).keys())
        //could include last month date
        const dayNumArr = numOfDays31
          .map((num) => dayjs().subtract(num, 'day').format('MM/DD/YYYY'))
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

        return dayNumArr.map((day) => day.slice(0, 5))
      }

      case '1w': {
        const numOfDays7 = Array.from(Array(7).keys())
        const last7DayArr = numOfDays7.map((num) =>
          dayjs().subtract(num, 'day').format('MM/DD/YYYY')
        )

        last7DayArr.sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        )

        last7DayArr.sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        )
        return last7DayArr
      }

      default:
        return monthNameArr
    }
  }

  const removeDuplicatesInArr = (arr: any[]) => Array.from(new Set(arr))

  // data arr for <LineChart>
  const renderChartData = () => {
    const labelsArr = renderXLabels()
    const chartDataArr: LineDataItem[] = []

    // Determine which records to use based on selected substance
    let recordsToUse = selectedRecordsForGraph
    if (selectedTab === 'SubstanceCounter' && selectedSubstance !== null) {
      // Use selected substance for SubstanceCounter view
      recordsToUse = selectedRecordsForGraph.filter(
        (record: SubstanceRecord) =>
          record.trackedSubstanceId === selectedSubstance
      )
    }

    const getDateWithMonth = (date: Date, months: number) => {
      date.setMonth(date.getMonth() + months)
      return date
    }

    // Average of 1 week (13)
    if (
      selectedTimeRange === '3m' ||
      selectedTimeRange === '6m' ||
      selectedTimeRange === '12m'
    ) {
      const dateXMonthAgoDate = getDateWithMonth(
        new Date(),
        -parseInt(selectedTimeRange)
      )
      const dayJsXMonthAgo = dayjs(dateXMonthAgoDate)
      const currentDate = dayjs()
      const daysCount =
        currentDate.diff(dayJsXMonthAgo, 'date') / (1000 * 60 * 60 * 24)

      //past x months days

      //past x months days
      if (daysCount) {
        const numOfXMDays = Array.from(Array(Math.floor(daysCount)).keys())
        const lastXMouthDateArr = numOfXMDays.map(
          (num) => dayjs().subtract(num, 'day').format('YYYY-MM-DD') //somehow need different format for react native from web
        )
        const weeksOfXMRaw = lastXMouthDateArr.map((date: string) => {
          return `${dayjs(date).get('year')}-${dayjs(date).week()}`
        })

        // arr: all weeks (removed duplicates) of past 3m/6m/12m; format: [{week: num, year: num}]
        const weeksOfXMNoDuplicates =
          removeDuplicatesInArr(weeksOfXMRaw).reverse()

        const BACdataObjTemp: {
          [index: string]: {
            value: number
            label: string
            hideDataPoint: boolean
          }
        } = {}

        // pre-populate BACdataObjTemp with all week labels
        weeksOfXMNoDuplicates.forEach((yearWeek: string) => {
          const week = yearWeek.split('-')[1]
          const year = yearWeek.split('-')[0]
          BACdataObjTemp[yearWeek] = {
            value: 0,
            label: dayjs(year).week(parseInt(week)).format('MM/DD'),
            hideDataPoint: true,
          }
        })

        // process real data, return an obj {week: {record, record..}}
        const groupRecordsByWeekKey = (recordsToUse as any[]).reduce(
          (groupedObj: any, eachObj: any) => {
            const week = dayjs(eachObj.recordDate).week()
            const year = dayjs(eachObj.recordDate).year()
            if (!groupedObj[`${year}-${week}`]) {
              groupedObj[`${year}-${week}`] = []
            }

            // For SubstanceCounter, calculate total intake
            const value =
              selectedTab === 'SubstanceCounter' &&
              'trackedSubstance' in eachObj
                ? calculateTotalIntake(eachObj as SubstanceRecord)
                : eachObj.value

            groupedObj[`${year}-${week}`]!.push(value)
            return groupedObj
          },
          {}
        )
        // return an obj {year-week1: averageRecord1, year-week2: averageRecord2, ...}
        const weekKeyWithAverageLevels = Object.fromEntries(
          Object.entries(groupRecordsByWeekKey).map(([key, value]: any) => [
            key,
            value.reduce((a: number, e: number) => a + e, 0) / value.length,
          ])
        )

        // modify BACdataObjTemp with actual values
        Object.keys(weekKeyWithAverageLevels).forEach((yearWeek: string) => {
          if (BACdataObjTemp[yearWeek]) {
            BACdataObjTemp[yearWeek]!.value =
              weekKeyWithAverageLevels[yearWeek] || 0
            BACdataObjTemp[yearWeek].hideDataPoint = false
          }
        })

        const displayLabel = (
          yearWeek: string,
          i: number,
          arrLength: number
        ) => {
          const [year, weekStr] = yearWeek.split('-')
          const week = parseInt(weekStr)
          if (i === 0) {
            return dayjs(dateXMonthAgoDate).format('MM/DD/YYYY')
          } else if (i === arrLength - 1) {
            return dayjs(currentDate).format('MM/DD/YYYY')
          } else if (
            selectedTimeRange === '3m' &&
            i === Math.floor(arrLength * 0.5)
          ) {
            return dayjs(year).week(week).format('MM/DD/YYYY')
          } else if (
            selectedTimeRange === '6m' &&
            (i === Math.floor(arrLength * 0.35) ||
              i === Math.floor(arrLength * 0.65))
          ) {
            return dayjs(year).week(week).format('MM/DD/YYYY')
          } else if (
            selectedTimeRange === '12m' &&
            (i === Math.floor(arrLength * 0.35) ||
              i === Math.floor(arrLength * 0.65))
          ) {
            return dayjs(year).week(week).format('MM/DD/YYYY')
          } else return ''
        }

        const arrLength = Object.keys(BACdataObjTemp).length

        Object.keys(BACdataObjTemp).forEach((yearWeek: string, i: number) => {
          chartDataArr.push({
            value: weekKeyWithAverageLevels[yearWeek]
              ? Number(
                  isFloat(Number(weekKeyWithAverageLevels[yearWeek]))
                    ? Number(
                        Number(weekKeyWithAverageLevels[yearWeek])
                      ).toFixed(3)
                    : Number(Number(weekKeyWithAverageLevels[yearWeek]))
                )
              : 0,
            label: displayLabel(yearWeek, i, arrLength),
            hideDataPoint: BACdataObjTemp[yearWeek].hideDataPoint,
          })
        })
      }
    }

    // Average of 1 Day (28-31)
    if (selectedTimeRange === '1m') {
      labelsArr.forEach((label: string) => {
        let hasData = false
        let dataValue = 0
        let dataPointText = 0

        recordsToUse.forEach((record: any) => {
          if (
            dayjs(record.recordDate).format('MM/DD') === label &&
            dayjs(record.recordDate).year() === dayjs().year() // make sure we're looking at current year's data
          ) {
            const value =
              selectedTab === 'SubstanceCounter' && 'trackedSubstance' in record
                ? calculateTotalIntake(record as SubstanceRecord)
                : record.value
            dataValue = value
            hasData = true
            if (selectedTab === 'BAC' && 'bacCount' in record) {
              dataPointText = record.bacCount
            }
          }
        })

        chartDataArr.push({
          value: dataValue,
          label: label,
          hideDataPoint: !hasData,
          dataPointText: dataPointText,
        })
      })

      chartDataArr.forEach((obj: LineDataItem, i: number) => {
        if (i % 5 !== 0) {
          obj.label = ''
        }
      })
    }

    // Average of 1 day (7)
    if (selectedTimeRange === '1w') {
      labelsArr.forEach((label: string) => {
        let hasData = false
        let dataValue = 0
        let dataPointText = 0

        recordsToUse?.forEach((record: any) => {
          if (dayjs(record.recordDate).format('MM/DD/YYYY') === label) {
            const value =
              selectedTab === 'SubstanceCounter' && 'trackedSubstance' in record
                ? calculateTotalIntake(record as SubstanceRecord)
                : record.value
            dataValue = value
            hasData = true
            if (selectedTab === 'BAC' && 'bacCount' in record) {
              dataPointText = record.bacCount
            }
          }
        })

        chartDataArr.push({
          value: dataValue,
          label: label.slice(0, 5),
          hideDataPoint: !hasData,
          dataPointText: dataPointText,
        })
      })
    }

    return chartDataArr
  }

  const renderLineSegments = () => {
    const graphDataArr = renderChartData()
    const lineSegments: LineSegment[] = []
    graphDataArr.forEach((dataObj: LineDataItem, i: number) => {
      if (dataObj.hideDataPoint === true) {
        // push two objects is because we can't have overlapping index objects
        lineSegments.push({
          startIndex: i - 1,
          endIndex: i,
          color: 'transparent',
        })
        lineSegments.push({
          startIndex: i,
          endIndex: i + 1,
          color: 'transparent',
        })
      }
    })
    return lineSegments
  }

  const renderChartXSpacing = () => {
    switch (selectedTimeRange) {
      case '12m': {
        return 10
      }
      case '6m': {
        return 20
      }
      case '3m': {
        return 40
      }
      case '1m': {
        return 20
      }
      case '1w': {
        return 105
      }
      default: {
        return 105
      }
    }
  }

  const getMaxValue = () => {
    if (selectedTab === 'SubstanceCounter' && selectedSubstance !== null) {
      const recordsToUse = selectedRecordsForGraph.filter(
        (record: SubstanceRecord) =>
          record.trackedSubstanceId === selectedSubstance
      )

      const maxValue = Math.max(
        ...recordsToUse.map((record) => calculateTotalIntake(record))
      )

      return maxValue || 1
    }

    return Math.max(...selectedRecordsForGraph.map((i) => i.value)) + 0.01 || 1
  }

  return (
    <div>
      <section className={classes.timeRangeTabs}>
        {timeRangeOptions.map((option, i) => (
          <button
            className={`${classes.timeRangeTabBtn} ${
              selectedTimeRange === option
                ? classes.timeRangeTabBtnSelected
                : ''
            }`}
            onClick={() => setSelectedTimeRange(option)}
            key={`timeRangeOption-${i}`}
          >
            <span className={classes.timeRangeTabBtnTxt}>{option}</span>
          </button>
        ))}
      </section>

      {/* Substance selection radio buttons - only show for SubstanceCounter tab */}
      {selectedTab === 'SubstanceCounter' && availableSubstances.length > 0 && (
        <section
          style={{
            margin: '16px 0',
            padding: '12px',
            backgroundColor: themeColors.grayWhite,
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              marginBottom: '8px',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
          >
            Select Substance:
          </div>

          {/* radio buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {availableSubstances.map((substance) => (
              <label
                key={substance.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  backgroundColor: themeColors.white,
                  transition: 'all 0.2s ease',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%', // Circular for radio button
                    backgroundColor:
                      selectedSubstance === substance.id
                        ? themeColors.darkPurple
                        : themeColors.gray60,
                    border: themeColors.darkPurple,
                    marginRight: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {selectedSubstance === substance.id && (
                    <div
                      style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: themeColors.darkPurple,
                      }}
                    />
                  )}
                </div>
                <input
                  type='radio'
                  name='substanceSelection'
                  value={substance.id}
                  checked={selectedSubstance === substance.id}
                  onChange={() => handleSubstanceSelection(substance.id)}
                  style={{ display: 'none' }} // Hide the default radio button
                />
                <span
                  style={{
                    color:
                      selectedSubstance === substance.id
                        ? themeColors.darkPurple
                        : themeColors.gray60,
                    fontWeight:
                      selectedSubstance === substance.id ? 'bold' : 'normal',
                  }}
                >
                  {substance.name} ({substance.unit})
                </span>
              </label>
            ))}
          </div>

          {selectedSubstance !== null && (
            <div
              style={{
                marginTop: '8px',
                fontSize: '12px',
                color: themeColors.darkPurple,
              }}
            >
              Y-axis unit: {getPrimarySubstanceUnit()}
            </div>
          )}
        </section>
      )}

      <section>
        <LineChart
          data={renderChartData()}
          disableScroll={true}
          showScrollIndicator
          spacing={renderChartXSpacing()}
          thickness={3}
          containerHeight={700}
          yAxisExtraHeight={20}
          width={window.innerWidth * 0.9}
          backgroundColor={themeColors.white}
          xAxisColor={themeColors.grayWhite}
          xAxisThickness={2}
          yAxisColor={themeColors.grayWhite}
          yAxisThickness={2}
          yAxisLabelWidth={55}
          minValue={0}
          maxValue={getMaxValue()}
          yAxisLabelTexts={
            selectedTab === 'MoodTracker'
              ? ['', '😫', '😔', '😐', '🙂', '😊', '😄']
              : null
          }
          yAxisTextStyle={{
            fontSize: selectedTab === 'MoodTracker' ? 22 : 16,
          }}
          yAxisLabelFormatter={(value: number) => `${value.toFixed(3)}`}
          roundToDigits={getMaxValue() > 200 ? 0 : 3} // yAxis
          noOfSections={6} // yAxis 6 sections
          showFractionalValues={true} //yAxis
          lineSegments={renderLineSegments()}
          dataPointsHeight={1}
          dataPointsWidth={1}
          dataPointsColor1={
            viewBacChartTab === 'Average'
              ? themeColors.darkPurple
              : themeColors.green
          }
          color={
            viewBacChartTab === 'Average'
              ? themeColors.darkPurple
              : themeColors.green
          }
          // text above dataPoints
          textColor1={themeColors.darkPurple}
          textShiftY={-6}
          textShiftX={-2}
          textFontSize={14}
        />
      </section>
    </div>
  )
}
